import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { combineLatest } from 'rxjs/observable/combineLatest';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { environment } from '../../environments/environment';
import { CeReferenceList, CeReference } from '../models/ce-reference';
import { ManuscriptList } from '../models/manuscript';
import { BiblioRecordList } from '../models/biblio-record';
import { GetMsTitlePipe } from '../pipes/get-ms-title.pipe';

@Injectable()
export class CeReferenceService {
    private httpHeaders: HttpHeaders;
    private msReferences$: BehaviorSubject<CeReferenceList>;
    private bibReferences$: BehaviorSubject<CeReferenceList>;

    constructor(
        private http: HttpClient,
        private getMsTitlePipe: GetMsTitlePipe
    ) {
        this.init();
    }

    init(): void {
        /* Common headers */
        this.httpHeaders = new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': environment.apiKey,
            'X-PhiBor-App': environment.phiborApp
        });

        this.msReferences$ = new BehaviorSubject<CeReferenceList>([]);
        this.bibReferences$ = new BehaviorSubject<CeReferenceList>([]);
    }

    /* Manuscript references */
    private loadMsReferences(refIdList: Array<number>): void {
        this.msReferences$.next([]);
        if (refIdList && refIdList.length > 0) {
            const msIdList = refIdList.join(',');

            if (msIdList) {
                this.http.get<ManuscriptList>(
                    environment.endpoints.de + '/manuscripts',
                    {
                        headers: this.httpHeaders,
                        params: new HttpParams()
                            .set('fields', 'pkid,fkidms_types,shelf_mark,shelf_mark_norm,private_collection,suffix,fkidlibraries,ed_witness')
                            .set('expand', 'fkidcountries,fkidcities,fkidlibraries,__works,__date')
                            .set('sort', 'fkidcountries,fkidcities,fkidlibraries,shelf_mark_norm')
                            .set('filter', '{"term":{"pkid":[' + msIdList + ']}}')
                    })
                    .subscribe(
                        msList => {
                            if (msList && msList.length > 0) {
                                this.msReferences$.next(
                                    msList.map(ms => new CeReference(ms.pkid.toString(), this.getMsTitlePipe.transform(ms, 'short'), ms))
                                );
                            }
                        },
                        error => this.msReferences$.error(error)
                    );
            }
        }
    }

    /* Bibliographic references */
    private loadBibReferences(refIdList: Array<number>): void {
        this.bibReferences$.next([]);
        if (refIdList && refIdList.length > 0) {
            const bibIdList = refIdList.join(',');

            if (bibIdList) {
                this.http.get<BiblioRecordList>(
                    environment.endpoints.de + '/biblio',
                    {
                        headers: this.httpHeaders,
                        params: new HttpParams()
                            .set('fields', 'pkid,fkidstatus,quotation_label,title,data_journal_title,fkidbiblio_types,reference'
                                + ',data_volume,data_volume_title,data_num_volumes,data_conference,data_url,data_consultation_date'
                                + ',data_dissertation_date,data_university,data_year,data_fascicle_number,data_pages'
                                + ',notes_text,data_edition_place,data_edition_date,data_publisher,data_book_series'
                                + ',data_event,fkidbiblio_comm_types,version')
                            .set('expand', 'fkidbiblio_types,fkidbiblio_comm_types,__linkedBiblio,__resps')
                            .set('sort', 'reference_sort')
                            .set('filter', '{"term":{"pkid":[' + bibIdList + ']}}')
                    })
                    .subscribe(
                        bibList => {
                            if (bibList && bibList.length > 0) {
                                this.bibReferences$.next(
                                    bibList.map(bib => new CeReference(bib.pkid.toString(), bib.quotation_label, bib))
                                );
                            }
                        },
                        error => this.bibReferences$.error(error)
                    );
            }
        }
    }

    /* Get manuscript references */
    getMsReferences(refIdList: Array<number>): Observable<CeReferenceList> {
        this.loadMsReferences(refIdList);
        return this.msReferences$.asObservable();
    }

    /* Get bibliographic references */
    getBibReferences(refIdList: Array<number>): Observable<CeReferenceList> {
        this.loadBibReferences(refIdList);
        return this.bibReferences$.asObservable();
    }

    /* Get all manuscript and bibliographic references */
    getAllReferences(msRefIdList: Array<number>, bibRefIdList: Array<number>): Observable<CeReferenceList[]> {
        this.loadMsReferences(msRefIdList);
        this.loadBibReferences(bibRefIdList);
        return combineLatest([this.msReferences$.asObservable(), this.bibReferences$.asObservable()]);
    }

}



// WEBPACK FOOTER //
// ./src/app/services/ce-reference.service.ts